import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useRecoilValue } from "recoil";
import { scopePreferenceState, activeScopeState } from "../global-state";
import {
  Select,
  MenuItem,
  FormControl,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mhome/ui";
import useMediaQuery from "@mui/material/useMediaQuery";
import { StaticTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import NumberUnitInput from "./number-unit-input";
import { safeAreaInsetsState } from "../global-state";

dayjs.extend(customParseFormat);

// Helper function to format time based on user preference or default
const formatTimeWithPreference = (time, timeFormat = null) => {
  if (!timeFormat) {
    // Default format: HH:mm:ss (24-hour with seconds)
    return time;
  }

  // Use user preferred format
  const dayTime = dayjs(time, "HH:mm:ss");
  if (!dayTime.isValid()) {
    return time; // fallback to original if parsing fails
  }

  switch (timeFormat) {
    case "HH:mm":
      return dayTime.format("HH:mm");
    case "hh:mm a":
      return dayTime.format("hh:mm A");
    default:
      return time; // fallback to original
  }
};

const SPECIAL_TIMES = [
  { value: "SUNRISE", label: "Sunrise" },
  { value: "SUNSET", label: "Sunset" },
  { value: "NOON", label: "Noon" },
  { value: "DAWN", label: "Dawn" },
  { value: "DUSK", label: "Dusk" },
];

const TimeGeneralInput = ({ valueSpec, onChange, zIndex = 100 }) => {
  const [tabValue, setTabValue] = useState(0); // 0 = clock time, 1 = special time
  const [clockTime, setClockTime] = useState("12:00:00");
  const [clockTimeValue, setClockTimeValue] = useState(null);
  const [specialTime, setSpecialTime] = useState("SUNRISE");
  const [offsetValue, setOffsetValue] = useState(0);
  const [offsetUnit, setOffsetUnit] = useState("min");
  const [offsetSign, setOffsetSign] = useState("+");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isInvalidValue, setIsInvalidValue] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");
  const isReadOnly = valueSpec.readOnly;
  const scopePreference = useRecoilValue(scopePreferenceState);
  const activeScope = useRecoilValue(activeScopeState);
  const userTimeFormat = scopePreference?.[activeScope]?.timeFormat;
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);

  // Parse the current value to determine format and set initial state (only on first load)
  useEffect(() => {
    if (initialized) return; // Only run once during initialization

    if (!valueSpec.value) {
      setIsInvalidValue(true);
      setDisplayValue("unknown time");
      setInitialized(true);
      return;
    }

    const value = valueSpec.value;
    let hasValidFormat = false;

    // Check if it's a special time format (e.g., SUNRISE, SUNRISE+10min, SUNSET-1hour)
    const specialTimeWithOffsetRegex =
      /^(SUNRISE|SUNSET|NOON|DAWN|DUSK)([+-])(\d+(?:\.\d+)?)(min|hour|sec)$/;
    const specialTimeOnlyRegex = /^(SUNRISE|SUNSET|NOON|DAWN|DUSK)$/;

    const matchWithOffset = value.match(specialTimeWithOffsetRegex);
    const matchSpecialOnly = value.match(specialTimeOnlyRegex);

    if (matchWithOffset) {
      setTabValue(1);
      setSpecialTime(matchWithOffset[1]);
      setOffsetSign(matchWithOffset[2]);
      setOffsetValue(parseFloat(matchWithOffset[3]));
      setOffsetUnit(matchWithOffset[4]);
      hasValidFormat = true;
    } else if (matchSpecialOnly) {
      setTabValue(1);
      setSpecialTime(matchSpecialOnly[1]);
      setOffsetSign("+");
      setOffsetValue(0);
      setOffsetUnit("min");
      hasValidFormat = true;
    } else {
      // Check if it's a valid clock time format
      const hasSeconds = value?.split(":").length === 3;
      const format = hasSeconds ? "HH:mm:ss" : "HH:mm";
      const parsedValue = dayjs(value, format);
      if (
        parsedValue.isValid() &&
        (value.match(/^\d{1,2}:\d{2}:\d{2}$/) || value.match(/^\d{1,2}:\d{2}$/))
      ) {
        setTabValue(0);
        setClockTime(value);
        setClockTimeValue(parsedValue);
        hasValidFormat = true;
      }
    }

    if (hasValidFormat) {
      setIsInvalidValue(false);
      setDisplayValue("");
    } else {
      // Invalid format - just display the raw value with error styling
      setIsInvalidValue(true);
      setDisplayValue(value);
    }

    setInitialized(true);
  }, [valueSpec.value, initialized, userTimeFormat]);

  // Update clock time value when clockTime changes
  useEffect(() => {
    if (clockTime) {
      const hasSeconds = clockTime.split(":").length === 3;
      const format = hasSeconds ? "HH:mm:ss" : "HH:mm";
      const parsedValue = dayjs(clockTime, format);
      setClockTimeValue(parsedValue.isValid() ? parsedValue : null);
    }
  }, [clockTime]);

  // Note: Time format preference only affects display, not the stored value
  // The clockTime is always stored in HH:mm:ss format internally

  const handleSectionClick = (sectionIndex) => {
    console.log("handleSectionClick", sectionIndex);
    if (isReadOnly) return;
    setTabValue(sectionIndex);

    // Don't emit onChange immediately - let the user interact with the new section
    // The onChange will be called when they actually change values in the new section
  };

  const handleClockTimeChange = (data) => {
    console.log("time", data);
    if (isReadOnly) return;
    const h = data?.$H ?? 0;
    const m = data?.$m ?? 0;
    const s = data?.$s ?? 0;
    const time = `${h < 10 ? "0" + h : h}:${m < 10 ? "0" + m : m}:${
      s < 10 ? "0" + s : s
    }`;
    setClockTimeValue(data);
    setClockTime(time);
  };

  const handleSpecialTimeChange = (event) => {
    if (isReadOnly) return;
    const newSpecialTime = event.target.value;
    setSpecialTime(newSpecialTime);
  };

  const handleOffsetChange = (value, unit) => {
    if (isReadOnly) return;
    const numValue = parseFloat(value) || 0;
    setOffsetValue(numValue);
    setOffsetUnit(unit);
  };

  const handleOffsetSignChange = (event) => {
    if (isReadOnly) return;
    const newSign = event.target.value;
    setOffsetSign(newSign);
  };

  const getDisplayText = () => {
    if (isInvalidValue) {
      return displayValue;
    }

    if (tabValue === 0) {
      if (!clockTime) return "";
      return formatTimeWithPreference(clockTime, userTimeFormat);
    } else {
      if (!specialTime) return "";
      const offsetStr =
        offsetValue > 0 ? `${offsetSign}${offsetValue}${offsetUnit}` : "";
      const specialTimeLabel =
        SPECIAL_TIMES.find((st) => st.value === specialTime)?.label ||
        specialTime;
      return `${specialTimeLabel}${offsetStr}`;
    }
  };

  const handleDialogOpen = () => {
    if (!isReadOnly) {
      // Set default values for dialog if we don't have any values yet
      if (!clockTime && !specialTime) {
        setTabValue(0);
        setClockTime("12:00:00");
        setClockTimeValue(dayjs("12:00:00", "HH:mm:ss"));
        setSpecialTime("SUNRISE");
        setOffsetValue(0);
        setOffsetUnit("min");
        setOffsetSign("+");
      }
      setDialogOpen(true);
    }
  };

  const getCurrentValue = () => {
    if (tabValue === 0) {
      // Always return the full HH:mm:ss format for storage, regardless of display preference
      return clockTime || "12:00:00";
    } else {
      const offsetStr =
        offsetValue > 0 ? `${offsetSign}${offsetValue}${offsetUnit}` : "";
      return `${specialTime}${offsetStr}`;
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  const handleSave = () => {
    const currentValue = getCurrentValue();
    // Clear invalid state since we now have a valid value
    setIsInvalidValue(false);
    setDisplayValue("");
    onChange(currentValue);
    setDialogOpen(false);
  };

  // Create offset value spec for NumberUnitInput
  const offsetValueSpec = {
    value: offsetValue > 0 ? `${offsetValue}${offsetUnit}` : `0${offsetUnit}`,
    units: ["sec", "min", "hour"],
    stepUnits: ["sec", "min", "hour"],
    steps: [1, 1, 1],
    min: 0,
    max: null,
    readOnly: isReadOnly,
  };

  const renderContent = () => (
    <div
      className="flex flex-col w-full max-w-none max-h-none gap-0 overflow-auto"
      style={{
        height: isSmallScreen ? "calc(100vh - 64px)" : "100%",
      }}
    >
      {/* Clock Time Section */}
      <div
        onClick={() => handleSectionClick(0)}
        className="w-full relative p-4 transition-all duration-200"
        style={{
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor:
            tabValue === 0 ? "hsl(var(--primary))" : "hsl(var(--border))",
          zIndex: tabValue === 0 ? 2 : 1,
          cursor: isReadOnly ? "default" : "pointer",
          opacity: tabValue === 0 ? 1 : 0.4,
          backgroundColor:
            tabValue === 0 ? "hsl(var(--card))" : "rgba(0, 0, 0, 0.02)",
          pointerEvents: tabValue === 0 ? "auto" : isReadOnly ? "none" : "auto",
          WebkitTapHighlightColor: "transparent",
        }}
        onMouseEnter={(e) => {
          if (!isReadOnly) {
            e.currentTarget.style.borderColor =
              tabValue === 0 ? "hsl(var(--primary))" : "rgba(0, 0, 0, 0.3)";
            e.currentTarget.style.opacity = tabValue === 0 ? "1" : "0.6";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor =
            tabValue === 0 ? "hsl(var(--primary))" : "hsl(var(--border))";
          e.currentTarget.style.opacity = tabValue === 0 ? "1" : "0.4";
        }}
      >
        <Typography className="mb-1 font-bold text-sm">Clock Time</Typography>
        {tabValue === 0 ? (
          <div className="min-h-[300px] flex flex-col items-stretch justify-center">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StaticTimePicker
                value={clockTimeValue}
                onChange={handleClockTimeChange}
                readOnly={isReadOnly}
                displayStaticWrapperAs="desktop"
                slots={{
                  digitallClock: true,
                  actionBar: null,
                }}
                slotProps={{
                  toolbar: {
                    hidden: false,
                    toolbarFormat: "HH:mm:ss",
                  },
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      zIndex,
                      "& .MuiInputBase-root": {
                        height: "32px",
                        ...(isReadOnly && {
                          backgroundColor: "rgba(0, 0, 0, 0.05)",
                          cursor: "default",
                        }),
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 8px",
                        ...(isReadOnly && {
                          color: "rgba(0, 0, 0, 0.38)",
                          cursor: "default",
                          WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
                          "&::selection": {
                            backgroundColor: "transparent",
                          },
                        }),
                      },
                      ...(isReadOnly && {
                        "& .MuiIconButton-root": {
                          visibility: "hidden",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.15)",
                        },
                      }),
                    },
                  },
                  clock: {
                    disabled: isReadOnly,
                  },
                }}
                sx={{
                  width: "100%",
                  opacity: isReadOnly ? 0.7 : 1,
                  pointerEvents: isReadOnly ? "none" : "auto",
                  zIndex,
                  "& .MuiPickersLayout-root": {
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    zIndex,
                  },
                  "& .MuiPickersLayout-contentWrapper": {
                    margin: 0,
                    zIndex,
                  },
                  "& .MuiDialogActions-root": {
                    display: "none",
                  },
                  "& .MuiClock-root": {
                    margin: "0 auto",
                    zIndex,
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        ) : (
          <Typography className="text-sm text-secondary italic">
            {formatTimeWithPreference(clockTime || "12:00:00", userTimeFormat)}
          </Typography>
        )}
      </div>

      {/* Solar Time Section */}
      <div
        onClick={() => handleSectionClick(1)}
        className="w-full relative p-4 transition-all duration-200"
        style={{
          borderTop: tabValue === 1 ? "1px solid" : "none",
          borderBottom: "1px solid",
          borderColor:
            tabValue === 1 ? "hsl(var(--primary))" : "hsl(var(--border))",
          zIndex: tabValue === 1 ? 2 : 1,
          marginTop: tabValue === 1 ? "-1px" : 0,
          cursor: isReadOnly ? "default" : "pointer",
          opacity: tabValue === 1 ? 1 : 0.4,
          backgroundColor:
            tabValue === 1 ? "hsl(var(--card))" : "rgba(0, 0, 0, 0.02)",
          pointerEvents: tabValue === 1 ? "auto" : isReadOnly ? "none" : "auto",
          WebkitTapHighlightColor: "transparent",
        }}
        onMouseEnter={(e) => {
          if (!isReadOnly) {
            e.currentTarget.style.borderColor =
              tabValue === 1 ? "hsl(var(--primary))" : "rgba(0, 0, 0, 0.3)";
            e.currentTarget.style.opacity = tabValue === 1 ? "1" : "0.6";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor =
            tabValue === 1 ? "hsl(var(--primary))" : "hsl(var(--border))";
          e.currentTarget.style.opacity = tabValue === 1 ? "1" : "0.4";
        }}
      >
        <Typography className="mb-1 font-bold text-sm">Solar Time</Typography>
        {tabValue === 1 ? (
          <div className="flex flex-col gap-4 min-h-[100px]">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="min-w-[120px] flex-shrink-0">
                <NumberUnitInput
                  valueSpec={offsetValueSpec}
                  onChange={handleOffsetChange}
                  onError={() => {}}
                  zIndex={zIndex + 10}
                />
              </div>

              <FormControl className="min-w-[80px] flex-[0_0_auto]">
                <Select
                  value={offsetSign}
                  onChange={handleOffsetSignChange}
                  disabled={isReadOnly}
                  size="small"
                  className="text-center"
                  style={{
                    zIndex: zIndex + 10,
                  }}
                >
                  <MenuItem value="+">After</MenuItem>
                  <MenuItem value="-">Before</MenuItem>
                </Select>
              </FormControl>

              <FormControl className="min-w-[120px] flex-[1_1_auto]">
                <Select
                  value={specialTime}
                  onChange={handleSpecialTimeChange}
                  disabled={isReadOnly}
                  size="small"
                  style={{
                    zIndex: zIndex + 10,
                  }}
                >
                  {SPECIAL_TIMES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        ) : (
          <Typography className="text-sm text-secondary italic">
            {(() => {
              const offsetStr =
                offsetValue > 0
                  ? `${offsetSign}${offsetValue}${offsetUnit}`
                  : "";
              const specialTimeLabel =
                SPECIAL_TIMES.find((st) => st.value === specialTime)?.label ||
                specialTime;
              return `${specialTimeLabel}${offsetStr}`;
            })()}
          </Typography>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="flex flex-col w-full h-full overflow-hidden items-center justify-center"
      style={{ zIndex }}
    >
      <Button
        onClick={handleDialogOpen}
        variant="outlined"
        disabled={isReadOnly}
        className="w-full h-full text-sm justify-center normal-case"
        style={{
          opacity: isReadOnly ? 0.7 : 1,
          cursor: isReadOnly ? "default" : "pointer",
          backgroundColor: isReadOnly ? "rgba(0, 0, 0, 0.05)" : "transparent",
          ...(isInvalidValue && {
            borderColor: "hsl(var(--destructive))",
            color: "hsl(var(--destructive))",
          }),
        }}
        onMouseEnter={(e) => {
          if (isInvalidValue) {
            e.currentTarget.style.borderColor = "hsl(var(--destructive))";
            e.currentTarget.style.backgroundColor =
              "hsl(var(--destructive) / 0.1)";
          }
        }}
        onMouseLeave={(e) => {
          if (isInvalidValue) {
            e.currentTarget.style.borderColor = "hsl(var(--border))";
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      >
        {getDisplayText()}
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullScreen={isSmallScreen}
        PaperProps={{
          style: {
            zIndex: zIndex + 1,
            width: isSmallScreen ? "100%" : "600px",
            maxWidth: isSmallScreen ? "100%" : "600px",
            minWidth: isSmallScreen ? "100%" : "600px",
            height: isSmallScreen ? "100vh" : "90vh",
            paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
            backgroundColor: "hsl(var(--card))",
          },
        }}
      >
        <DialogContent
          style={{
            padding: 0,
            overflow: "auto",
            minHeight: "300px",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "hsl(var(--card))",
          }}
        >
          {renderContent()}
        </DialogContent>
        <DialogActions className="p-2 gap-1">
          <Button
            onClick={handleCancel}
            variant="outlined"
            className="shadow-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className="shadow-none"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TimeGeneralInput;
